import React, { useId } from 'react';
import { LOGO_VIEWBOX, LOGO_TRANSFORM, LOGO_PURPLE_D, LOGO_GREY_D } from '../assets/logoPaths';
import { WORDMARK_VIEWBOX, WORDMARK_TRANSFORM, WORDMARK_FAIR_D, WORDMARK_FIX_D } from '../assets/wordmarkPaths';

/**
 * FairFix logo system — faithful vector trace of the official logo pack.
 * Purple F (front) + second F and roofline (behind) over the small 2×2
 * window. Purple is the exact brand hex #873BD2 sampled from the artwork.
 *
 * On DARK surfaces the second F / roofline / window frame render in
 * near-white (#F5F5F8) so they read clearly against #0A0A0A / #111111.
 * On LIGHT surfaces they render in near-black (#1A1A1A). The purple F
 * and the small 2×2 window detail stay brand purple in both variants —
 * they carry the identity.
 */

const BRAND_PURPLE = '#873BD2';
// Colour token for whichever elements were previously "dark grey" —
// second F, roofline panels. Controlled per variant.
const SECONDARY_ON_DARK = '#F5F5F8';
const SECONDARY_ON_LIGHT = '#1A1A1A';
const [, , VB_W, VB_H] = LOGO_VIEWBOX.split(' ').map(Number);

// A separate purple silhouette glows behind the crisp original artwork.
// Outer viewBox units keep its halo proportional at every header size.
const PurpleGlowFilter = ({ id, width, height }) => (
  <defs>
    <filter id={id} filterUnits="userSpaceOnUse" x={-width * .3} y={-height}
      width={width * 1.6} height={height * 3} colorInterpolationFilters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="32" />
    </filter>
  </defs>
);

export const Monogram = ({ variant = 'dark', size = 46, title = 'FairFix logo', animate = false, glow = false }) => {
  const glowId = useId();
  const secondary =
    variant === 'light' ? SECONDARY_ON_LIGHT : SECONDARY_ON_DARK;
  const showBg = variant === 'appicon';
  const cls = animate ? 'ff-mark ff-mark-draw' : 'ff-mark';
  const pl = animate ? '1' : undefined;
  return (
    <svg
      width={size}
      height={Math.round(size * (VB_H / VB_W))}
      viewBox={LOGO_VIEWBOX}
      role="img"
      aria-label={title}
      className={cls}
      xmlns="http://www.w3.org/2000/svg"
    >
      {showBg && <rect x="0" y="0" width={VB_W} height={VB_H} rx={VB_W * 0.22} fill="#111111" />}
      {glow && <>
        <PurpleGlowFilter id={glowId} width={VB_W} height={VB_H} />
        <g className="brand-purple-halo" filter={`url(#${glowId})`} aria-hidden="true">
          <g transform={LOGO_TRANSFORM}><path fill="#b264ff" d={LOGO_PURPLE_D} /></g>
        </g>
      </>}
      <g transform={LOGO_TRANSFORM}>
        <path fill={secondary} stroke={secondary} pathLength={pl} d={LOGO_GREY_D} />
        <path fill={BRAND_PURPLE} stroke={BRAND_PURPLE} pathLength={pl} d={LOGO_PURPLE_D} />
      </g>
    </svg>
  );
};

/**
 * Wordmark: "FAIRFIX" traced directly from the owner's logo artwork.
 * Two path groups so FAIR/FIX can be coloured independently via CSS.
 * Falls back to the old Exo 2 text wordmark for legacy consumers that pass
 * a className to opt out via `text` variant.
 */
export const Wordmark = ({ variant = 'dark', className = '', glow = false }) => {
  const glowId = useId();
  return (
  <span className={`wordmark wordmark-${variant} wordmark-svg ${className}`.trim()} aria-label="FairFix" role="img">
    <svg
      className="wordmark-svg-el"
      viewBox={WORDMARK_VIEWBOX}
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {glow && <>
        <PurpleGlowFilter id={glowId} width={3786} height={660} />
        <g className="brand-purple-halo" filter={`url(#${glowId})`}>
          <g transform={WORDMARK_TRANSFORM} fillRule="evenodd"><path fill="#b264ff" d={WORDMARK_FAIR_D} /></g>
        </g>
      </>}
      <g transform={WORDMARK_TRANSFORM} fillRule="evenodd">
        <path className="wm-fair" d={WORDMARK_FAIR_D} />
        <path className="wm-fix" d={WORDMARK_FIX_D} />
      </g>
    </svg>
  </span>
  );
};

/** Tagline with short purple rules flanking it — small caps, wide tracking. */
export const Tagline = ({ align = 'center', className = '' }) => (
  <div className={`tagline tagline-${align} ${className}`.trim()}>
    <span className="tagline-rule" aria-hidden="true" />
    <span className="tagline-text">Quality Work. Fair Price. Built to Last.</span>
    <span className="tagline-rule" aria-hidden="true" />
  </div>
);

/** Monogram + wordmark lockup (navbar / footer). */
export const BrandLockup = ({ variant = 'dark', className = '', animate = false }) => (
  <span className={`brand-lockup ${className}`.trim()}>
    <Monogram variant={variant} size={48} animate={animate} />
    <span className="brand-lockup-text">
      <Wordmark variant={variant} />
    </span>
  </span>
);

/**
 * MonogramOutline — outlined (stroke-only, no fill) version of the FF
 * mark for use as a purely decorative watermark on the hero image.
 * `aria-hidden` is baked in — this element carries no meaning.
 * Colour and opacity are controlled by the consumer via CSS.
 */
export const MonogramOutline = ({ className = '' }) => (
  <svg
    className={`ff-mark-outline ${className}`.trim()}
    viewBox={LOGO_VIEWBOX}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    preserveAspectRatio="xMidYMid meet"
  >
    <g
      transform={LOGO_TRANSFORM}
      fill="none"
      stroke="currentColor"
      strokeWidth="34"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d={LOGO_GREY_D} />
      <path d={LOGO_PURPLE_D} />
    </g>
  </svg>
);

/**
 * HeroLockup — the big banner used above the hero H1 on desktop.
 * Pure SVG + text: monogram, vertical purple rule, "FAIRFIX" wordmark,
 * a descriptor line and a "HOBART" line flanked by short purple rules.
 * Zero image requests, pixel-perfect at any zoom, fully readable by
 * search engines and screen readers.
 */
export const HeroLockup = ({ className = '' }) => (
  <div
    className={`hero-lockup ${className}`.trim()}
    role="img"
    aria-label="FairFix — Qualified Carpenter · Residential Construction Specialist · Hobart"
    data-testid="hero-lockup"
  >
    <Monogram variant="dark" size={94} title="" />
    <span className="hero-lockup-rule" aria-hidden="true" />
    <div className="hero-lockup-text">
      <Wordmark variant="dark" className="hero-lockup-wordmark" />
      <span className="hero-lockup-descriptor-row" aria-hidden="true">
        <span className="hero-lockup-rule-h hero-lockup-rule-h--descriptor" />
        <span className="hero-lockup-descriptor">
          Qualified Carpenter &middot; Residential Construction Specialist
        </span>
        <span className="hero-lockup-rule-h hero-lockup-rule-h--descriptor" />
      </span>
      <span className="hero-lockup-hobart-row" aria-hidden="true">
        <span className="hero-lockup-rule-h" />
        <span className="hero-lockup-hobart">Hobart</span>
        <span className="hero-lockup-rule-h" />
      </span>
    </div>
  </div>
);
